import React, { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  isDownloadAppsRoute,
  PAGE_ROUTE_DOWNLOAD_APPS
} from "@kivunova/kivufrontendcommon";
import {
  markFirstVisitPopupDismissed,
  shouldShowFirstVisitDownloadPopup
} from "../../Utils/embeddedShellSession";

/**
 * Bottom sheet on first eligible visit until an installer link is used (localStorage), or “Not now” / “View more” dismisses for this session (sessionStorage),
 * or while on download-apps. Hidden when running in an embedded/exported app shell, or when `?seoIndex=1` is present.
 */
const FirstVisitDownloadDialog: React.FC = () => {
  const theme = useTheme();
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const i18n = useContext(KivuI18nContext);
  const language = i18n?.language;
  const defaultLang = i18n?.defaultLang;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isDownloadAppsRoute(location.pathname)) {
      setOpen(false);
      return;
    }
    setOpen(shouldShowFirstVisitDownloadPopup());
  }, [location.pathname, location.search]);

  const handleViewMore = () => {
    markFirstVisitPopupDismissed();
    setOpen(false);
    if (language != null && defaultLang != null) {
      navigate(localizedPath(PAGE_ROUTE_DOWNLOAD_APPS, language, defaultLang));
    } else {
      navigate(`/${PAGE_ROUTE_DOWNLOAD_APPS}`);
    }
  };

  const handleDismiss = () => {
    markFirstVisitPopupDismissed();
    setOpen(false);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleDismiss}
      aria-labelledby="first-visit-download-dialog-title"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          left: 0,
          right: 0,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          overflow: "hidden",
          boxShadow: "0 -8px 40px rgba(0, 0, 0, 0.12)",
          pb: "max(16px, env(safe-area-inset-bottom, 0px))",
          backgroundColor: "#fff"
        },
        role: "dialog"
      }}
      ModalProps={{
        keepMounted: false
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "100vw"
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.45)"
        }
      }}
    >
      <Box sx={{ position: "relative" }}>
        {/* Drag affordance (visual only; dismiss via close / Not now / backdrop) */}
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.25),
            mx: "auto",
            mt: 1.5,
            mb: 0.5
          }}
          aria-hidden
        />
        <IconButton
          aria-label={t("first-visit-download-dialog-close", "Close")}
          onClick={handleDismiss}
          size="small"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary"
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack
          spacing={2}
          alignItems="center"
          textAlign="center"
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            maxWidth: 520,
            width: "100%",
            mx: "auto"
          }}
        >
          <Typography
            id="first-visit-download-dialog-title"
            component="h2"
            variant="h6"
            fontWeight={700}
            color="text.primary"
            sx={{ pr: 0, px: 1 }}
          >
            {t(
              "first-visit-download-dialog-title",
              "Get Kivunova FMS on your devices"
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.65
            }}
          >
            {t(
              "first-visit-download-dialog-body",
              "Install the desktop app or mobile app for the best experience — same account as in the browser."
            )}
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "100%",
              columnGap: "5px",
              rowGap: 1
            }}
          >
            <Button
              variant="text"
              color="primary"
              data-testid="first-visit-download-not-now"
              onClick={handleDismiss}
              sx={{
                fontWeight: 600,
                textTransform: "none"
              }}
            >
              {t("first-visit-download-dialog-not-now", "Not now")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              data-testid="first-visit-download-view-more"
              onClick={handleViewMore}
              sx={{
                width: "fit-content",
                minWidth: 180,
                px: 4,
                py: 1.25,
                borderRadius: 999,
                fontWeight: 700,
                whiteSpace: "nowrap"
              }}
            >
              {t("first-visit-download-dialog-view-more", "View more")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default FirstVisitDownloadDialog;
